import React from "react";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import { Delete } from "@dreamhub/designsystem/icons";
import {
  PricingTableProps,
  PricingTableRow,
  ServiceDuration,
  BillingFrequency,
  ChargeType
} from "../types";
import EditableSelectCell from "./EditableSelectCell";
import EditablePriceCell from "./EditablePriceCell";
import {
  BILLING_FREQUENCY_OPTIONS,
  SERVICE_DURATION_OPTIONS
} from "@/pages/settings/pages/products-v2/consts";
import { useTranslation } from "react-i18next";

const TRANSLATION_KEY = "settings.product";

export const PricingTable: React.FC<PricingTableProps> = ({
  pricing,
  onPricingChange,
  chargeType
}) => {
  const { t } = useTranslation();
  const generateId = () =>
    `pricing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleAddPricing = () => {
    const newRow: PricingTableRow = {
      id: generateId(),
      serviceItemPeriods: null,
      billingFrequency: null,
      price: 0,
      convertedPrice: null
    };
    onPricingChange([...pricing, newRow]);
  };

  const handleDeletePricing = (id: string) => {
    onPricingChange(pricing.filter((row) => row.id !== id));
  };

  const handleRowChange = (
    id: string,
    field: keyof PricingTableRow,
    value: any
  ) => {
    const updatedPricing = pricing.map((row) => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    });
    onPricingChange(updatedPricing);
  };

  const translateOptions = (
    options: Array<{ id: number; tKey: string; subtitleTKey?: string }>
  ) =>
    options.map((option) => ({
      id: option.id,
      displayName: t(option.tKey),
      subtitle: option.subtitleTKey ? t(option.subtitleTKey) : undefined
    }));

  const getBillingFrequencyOptions = () => {
    const filteredOptions =
      Number(chargeType) === ChargeType.ONE_TIME
        ? BILLING_FREQUENCY_OPTIONS.filter(
            (option) =>
              option.id === BillingFrequency.ONCE ||
              option.id === BillingFrequency.CUSTOM
          )
        : BILLING_FREQUENCY_OPTIONS;
    return translateOptions(filteredOptions);
  };

  const serviceDurationOptions = translateOptions(SERVICE_DURATION_OPTIONS);
  const billingFrequencyOptions = getBillingFrequencyOptions();

  return (
    <div className="w-full" data-test="products-pricing-table">
      <div className="mb-1">
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_KEY}.pricingTable.pricing`)} *
        </Text>
      </div>

      <div className="border border-border-border2 rounded-lg overflow-hidden mb-4">
        <div
          className="bg-surface-elevation2 border-b border-border-border2"
          data-test="products-pricing-table-header"
        >
          <div className="flex px-4 py-3">
            <div className="w-[120px]">
              <Text variant="smLabel" className="text-secondary">
                {t(`${TRANSLATION_KEY}.pricingTable.serviceDuration`)}
              </Text>
            </div>
            <div className="w-[120px]">
              <Text variant="smLabel" className="text-secondary">
                {t(`${TRANSLATION_KEY}.pricingTable.billingFrequency`)}
              </Text>
            </div>
            <div className="w-[150px]">
              <Text variant="smLabel" className="text-secondary">
                {t(`${TRANSLATION_KEY}.pricingTable.priceForEntirePeriod`)}
              </Text>
            </div>
            <div className="flex-1"></div>
          </div>
        </div>

        <div className="divide-y divide-border-border2">
          {pricing.map((row) => (
            <div
              key={row.id}
              data-test={`products-pricing-row-${row.id}`}
              className="flex items-center px-2 bg-surface-elevation2 hover:bg-surface-hover-soft transition-colors min-h-[44px] max-h-[55px]"
            >
              <div className="w-[120px] flex items-center">
                <EditableSelectCell
                  value={row.serviceItemPeriods}
                  options={serviceDurationOptions}
                  placeholder="Select"
                  onValueChange={(value) =>
                    handleRowChange(
                      row.id,
                      "serviceItemPeriods",
                      Number(value) as ServiceDuration
                    )
                  }
                  data-test={`products-pricing-row-duration-${row.id}`}
                />
              </div>
              {Number(row.serviceItemPeriods) === ServiceDuration.CUSTOM ? (
                <>
                  <div
                    className="flex-1 flex items-center px-2 mr-4"
                    data-test={`products-pricing-row-custom-text-${row.id}`}
                  >
                    <Text variant="body" className="text-secondary">
                      {t(`${TRANSLATION_KEY}.pricingTable.definedPerDeal`)}
                    </Text>
                  </div>
                  <div className="flex items-center justify-end">
                    {pricing.length > 1 && (
                      <IconButton
                        variant="iconNoBg"
                        iconColor="secondary"
                        size="sm"
                        onClick={() => handleDeletePricing(row.id)}
                        data-test={`products-pricing-row-delete-${row.id}`}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-[120px] flex items-center">
                    <EditableSelectCell
                      value={row.billingFrequency}
                      options={billingFrequencyOptions}
                      placeholder="Select"
                      onValueChange={(value) =>
                        handleRowChange(
                          row.id,
                          "billingFrequency",
                          Number(value) as BillingFrequency
                        )
                      }
                      data-test={`products-pricing-row-frequency-${row.id}`}
                    />
                  </div>
                  <div className="w-[140px] flex items-center">
                    <EditablePriceCell
                      value={row.price}
                      onValueChange={(value) =>
                        handleRowChange(row.id, "price", value)
                      }
                      data-test={`products-pricing-row-price-${row.id}`}
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-end">
                    {pricing.length > 1 && (
                      <IconButton
                        variant="iconNoBg"
                        iconColor="secondary"
                        size="sm"
                        onClick={() => handleDeletePricing(row.id)}
                        data-test={`products-pricing-row-delete-${row.id}`}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="flex items-center bg-surface-elevation2 hover:bg-surface-hover-soft h-[44px] transition-colors">
            <div className="flex-1 flex items-center">
              <Button
                variant="tertiary"
                className="ml-2"
                onClick={handleAddPricing}
                data-test="products-pricing-table-add-button"
              >
                {t(`${TRANSLATION_KEY}.pricingTable.addPricing`)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
